import React, { useMemo } from "react";

const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TRAILING_PUNCT_REGEX = /[.,;:!?)}\]"']+$/;

const stripTrailingPunctuation = (url) => {
  let cleaned = url;
  let trailing = "";
  while (TRAILING_PUNCT_REGEX.test(cleaned)) {
    const match = cleaned.match(TRAILING_PUNCT_REGEX);
    if (!match) break;
    trailing = match[0] + trailing;
    cleaned = cleaned.slice(0, -match[0].length);
  }
  return { url: cleaned, trailing };
};

const normalizeHref = (url) => (/^www\./i.test(url) ? `https://${url}` : url);

const parseTextWithLinks = (text) => {
  const value = String(text ?? "");
  if (!value) return [];

  const parts = [];
  let lastIndex = 0;
  const regex = new RegExp(URL_REGEX.source, URL_REGEX.flags);
  let match = regex.exec(value);

  while (match) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: value.slice(lastIndex, match.index) });
    }

    const raw = match[0];
    const { url, trailing } = stripTrailingPunctuation(raw);
    if (url) {
      parts.push({ type: "link", value: url });
    }
    if (trailing) {
      parts.push({ type: "text", value: trailing });
    }

    lastIndex = match.index + raw.length;
    match = regex.exec(value);
  }

  if (lastIndex < value.length) {
    parts.push({ type: "text", value: value.slice(lastIndex) });
  }

  return parts;
};

export function LinkifiedText({ text, className = "", linkClassName = "underline text-[#2563EB] hover:text-[#1D4ED8]" }) {
  const parts = useMemo(() => parseTextWithLinks(text), [text]);

  if (!parts.length) {
    return null;
  }

  if (parts.length === 1 && parts[0].type === "text") {
    return <span className={className}>{parts[0].value}</span>;
  }

  return (
    <span className={className}>
      {parts.map((part, index) =>
        part.type === "link" ? (
          <a
            key={`link-${index}-${part.value}`}
            href={normalizeHref(part.value)}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
            onClick={(event) => event.stopPropagation()}
          >
            {part.value}
          </a>
        ) : (
          <React.Fragment key={`text-${index}`}>{part.value}</React.Fragment>
        )
      )}
    </span>
  );
}
